import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { Button, Card, FormField, Input } from '../../components/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';

/**
 * Owner's punch-list item 3 (client-staff forgot/reset password): the server's
 * `POST /dashboard/auth/forgot-password` always returns the exact same generic `{ok: true}`
 * response regardless of whether `email`/`tenantSlug` matched a real account (no account
 * enumeration) - this page's UI mirrors that: it always shows the same success message after a
 * submit that reaches the server at all, never a different one for "found" vs. "not found".
 */
export function ForgotPasswordPage() {
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<ParsedApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.auth.forgotPassword(email, tenantSlug);
      setSubmitted(true);
    } catch (err) {
      setError(toParsedError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg px-4">
      <Card className="w-full max-w-sm" title="Reset your password">
        {submitted ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              If an account exists for that email at that organization, we've sent a link to reset
              its password. The link expires in 48 hours.
            </p>
            <Link to="/login" className="text-sm text-brand-primary underline text-center">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form className="flex flex-col gap-1" onSubmit={handleSubmit}>
            <p className="mb-3 text-sm text-slate-600">
              Enter your organization slug and email - if an account exists, we'll send a link to
              reset its password.
            </p>
            <FormField label="Organization slug" htmlFor="forgot-tenant" hint="e.g. acme-live">
              <Input
                id="forgot-tenant"
                autoComplete="organization"
                autoFocus
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
              />
            </FormField>
            <FormField label="Email" htmlFor="forgot-email">
              <Input
                id="forgot-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormField>
            {error && <ErrorSummary error={error} className="mb-1" />}
            <Button type="submit" disabled={submitting} className="w-full justify-center mt-2">
              {submitting ? 'Sending...' : 'Send reset link'}
            </Button>
            <Link
              to="/login"
              className="mt-2 text-center text-sm text-slate-500 hover:text-slate-700"
            >
              Back to sign in
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}
